// Lightweight API client for the public site + admin portal.
const BACKEND = process.env.REACT_APP_BACKEND_URL || "";
const API = `${BACKEND}/api`;
const TOKEN_KEY = "cli_admin_token";

// Turn a "/api/media/xxx" path into an absolute URL against the backend.
export function absoluteUrl(u) {
  if (!u) return u;
  if (/^https?:\/\//i.test(u)) return u;
  if (u.startsWith("/api/")) return `${BACKEND}${u}`;
  return u;
}

export function getToken() {
  try {
    return localStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
}

export function setToken(token) {
  try {
    if (token) localStorage.setItem(TOKEN_KEY, token);
    else localStorage.removeItem(TOKEN_KEY);
  } catch {
    /* ignore */
  }
}

export function clearToken() {
  setToken(null);
}

// A browser trusted with "don't ask again on this device" keeps its proof
// across sign-outs — that is the whole point, so this is deliberately not
// cleared with the session token. Tokens are kept per account so two people
// sharing a machine don't overwrite each other's.
const DEVICE_KEY = "cli_admin_devices";

function deviceMap() {
  try {
    return JSON.parse(localStorage.getItem(DEVICE_KEY) || "{}") || {};
  } catch {
    return {};
  }
}

export function getDeviceToken(email) {
  return deviceMap()[String(email || "").toLowerCase()] || null;
}

export function setDeviceToken(email, token) {
  try {
    const map = deviceMap();
    const key = String(email || "").toLowerCase();
    if (token) map[key] = token;
    else delete map[key];
    localStorage.setItem(DEVICE_KEY, JSON.stringify(map));
  } catch {
    /* ignore */
  }
}

// Lets AdminAuthProvider notice a session invalidated mid-use (expired/revoked
// token) so it can drop its auth state and let the router redirect to login,
// instead of the shell rendering on for a token that no longer works.
const sessionExpiredListeners = new Set();
export function onSessionExpired(cb) {
  sessionExpiredListeners.add(cb);
  return () => sessionExpiredListeners.delete(cb);
}

async function request(path, { method = "GET", body, auth = false, keepSession = false } = {}) {
  const headers = { "Content-Type": "application/json" };
  if (auth) {
    const t = getToken();
    if (t) headers.Authorization = `Bearer ${t}`;
  }
  const res = await fetch(`${API}${path}`, {
    method,
    headers,
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  if (!res.ok) {
    // keepSession: a 401/403 from a proxied sister-site call must not be
    // mistaken for THIS admin's session expiring — don't log the user out.
    if ((res.status === 401 || res.status === 403) && auth && !keepSession) {
      clearToken();
      sessionExpiredListeners.forEach((cb) => cb());
    }
    let detail = res.status === 401 || res.status === 403 ? "Unauthorized" : `Request failed (${res.status})`;
    try {
      const j = await res.json();
      if (j && j.detail) detail = typeof j.detail === "string" ? j.detail : JSON.stringify(j.detail);
    } catch {
      /* ignore */
    }
    const err = new Error(detail);
    err.status = res.status;
    throw err;
  }
  if (res.status === 204) return null;
  const ct = res.headers.get("content-type") || "";
  return ct.includes("application/json") ? res.json() : res.text();
}

// ---- Public ----
export const fetchContent = () => request("/content");

// Newsletter signup → creates a lead (source: "newsletter"). Reuses /contact.
export const submitNewsletter = (email, extra = {}) =>
  request("/contact", { method: "POST", body: { name: (email || "").split("@")[0] || "Subscriber", email, source: "newsletter", service: "Newsletter", ...extra } });

// AI concierge chat widget — self-hosted on our own AI key, no third-party
// SaaS subscription. Stateless server-side: pass the last few turns back as
// `history` each call (see components/AiConcierge.jsx).
export const chatWithConcierge = (message, history = [], page) =>
  request("/assistant/chat", { method: "POST", body: { message, history, page } });

// Opening line + suggested questions for the page the visitor is reading.
export const fetchConciergeOpener = (page) =>
  request(`/assistant/opener?page=${encodeURIComponent(page || "/")}`);

// Neural voice for a reply. Returns an MP3 blob, or throws (409) when the
// premium voice is off/unconfigured so the caller falls back to browser speech.
export async function fetchConciergeSpeech(text, lang) {
  const res = await fetch(`${API}/assistant/speak`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ text, lang }),
  });
  if (!res.ok) {
    const err = new Error(`Voice unavailable (${res.status})`);
    err.status = res.status;
    throw err;
  }
  return res.blob();
}

// Thumbs up/down on one concierge reply (see the Assistant chats report).
export const rateConciergeReply = (id, helpful) =>
  request("/assistant/feedback", { method: "POST", body: { id, helpful } });

// What visitors asked the concierge — transcripts + the pages they asked from.
export const fetchAssistantChats = (days = 30) => request(`/admin/assistant/chats?days=${days}`, { auth: true });

// Fire-and-forget, cookie-free page-view beacon (never blocks or throws).
// Pass notFound=true from the 404 page so it feeds the Top-404s report.
export function recordPageview(path, ref, notFound = false) {
  try {
    const body = JSON.stringify({ path, ref: ref || undefined, nf: notFound || undefined });
    if (navigator.sendBeacon) {
      navigator.sendBeacon(`${API}/pageview`, new Blob([body], { type: "application/json" }));
    } else {
      fetch(`${API}/pageview`, { method: "POST", headers: { "Content-Type": "application/json" }, body, keepalive: true }).catch(() => {});
    }
  } catch {
    /* ignore */
  }
}

// ---- A/B testing ----
// Fire-and-forget exposure/conversion beacon (never blocks or throws).
export function recordAb(exp, variant, event = "view") {
  try {
    const body = JSON.stringify({ exp, variant, event });
    if (navigator.sendBeacon) {
      navigator.sendBeacon(`${API}/ab`, new Blob([body], { type: "application/json" }));
    } else {
      fetch(`${API}/ab`, { method: "POST", headers: { "Content-Type": "application/json" }, body, keepalive: true }).catch(() => {});
    }
  } catch {
    /* ignore */
  }
}

// Record a conversion for every experiment this visitor is enrolled in, once
// per session per experiment (call after a successful lead/newsletter submit).
export function recordAbConversions() {
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (!k || !k.startsWith("cli_ab_") || k.endsWith("_seen")) continue;
      const exp = k.slice("cli_ab_".length);
      const variant = localStorage.getItem(k);
      if (!variant) continue;
      const convKey = `${k}_converted`;
      if (sessionStorage.getItem(convKey) === "1") continue;
      sessionStorage.setItem(convKey, "1");
      recordAb(exp, variant, "convert");
    }
  } catch {
    /* ignore */
  }
}

export const fetchAbResults = (days = 90) => request(`/admin/ab/results?days=${days}`, { auth: true });

// The visitor's active A/B assignments, as label→variant pairs, so they can be
// attached to a lead's `extra` (shows which variant a converting visitor saw).
export function abExtra() {
  const out = {};
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (!k || !k.startsWith("cli_ab_") || k.endsWith("_seen")) continue;
      const variant = localStorage.getItem(k);
      if (variant) out[`A/B · ${k.slice("cli_ab_".length)}`] = variant;
    }
  } catch {
    /* ignore */
  }
  return out;
}

// ---- Traffic (admin) ----
export const fetchTraffic = (days = 30) => request(`/admin/analytics/traffic?days=${days}`, { auth: true });
export const fetchNotFound = (days = 90) => request(`/admin/analytics/not-found?days=${days}`, { auth: true });
export const fetchAutoDraft = () => request("/admin/ai/auto-draft", { auth: true });
export const runAutoDraftNow = () => request("/admin/ai/auto-draft/run", { method: "POST", auth: true });

// ---- Search Console (admin) ----
export const fetchSearchConsole = (days = 28) => request(`/admin/seo/search-console?days=${days}`, { auth: true });

// OpenSEO (app.openseo.so) — read-only data via the backend's MCP client.
export const fetchOpenSeoConfig = () => request("/admin/openseo/config", { auth: true });
export const saveOpenSeoConfig = (body) => request("/admin/openseo/config", { method: "POST", body, auth: true });
export const disconnectOpenSeo = () => request("/admin/openseo/disconnect", { method: "POST", auth: true });
export const fetchOpenSeoProjects = (refresh = false) => request(`/admin/openseo/projects${refresh ? "?refresh=true" : ""}`, { auth: true });
export const fetchOpenSeoOverview = (site = "clisecure", refresh = false) =>
  request(`/admin/openseo/overview?site=${encodeURIComponent(site)}${refresh ? "&refresh=true" : ""}`, { auth: true });
export const fetchOpenSeoReport = (id, site = "clisecure") =>
  request(`/admin/openseo/report/${encodeURIComponent(id)}?site=${encodeURIComponent(site)}`, { auth: true });
export const fetchOpenSeoSearchConsole = (site = "clisecure", range = "last_3_months", refresh = false) =>
  request(`/admin/openseo/search-console?site=${encodeURIComponent(site)}&range=${range}${refresh ? "&refresh=true" : ""}`, { auth: true });
// Live check of the SEO fixes (301s, 404s, canonicals, sitemap) against the public site.
export const fetchSeoHealthCheck = () => request("/admin/seo/health-check", { auth: true });
export const fetchBuiltinRedirects = () => request("/admin/seo/builtin-redirects", { auth: true });
// OpenSEO Auto-fix (titles/descriptions), consolidation hubs, audit re-run.
export const previewOpenSeoAutofix = (paths) => request("/admin/openseo/autofix/preview", { method: "POST", body: { site: "clisecure", ...(paths ? { paths } : {}) }, auth: true });
export const applyOpenSeoAutofix = (fixes) => request("/admin/openseo/autofix/apply", { method: "POST", body: { fixes }, auth: true });
export const runOpenSeoAudit = (site = "clisecure") => request(`/admin/openseo/audit/run?site=${site}`, { method: "POST", auth: true });
export const fetchOpenSeoAuditStatus = (site = "clisecure") => request(`/admin/openseo/audit/status?site=${site}`, { auth: true });
export const fetchConsolidation = () => request("/admin/openseo/consolidation", { auth: true });
export const previewConsolidation = (hub) => request("/admin/openseo/consolidation/preview", { method: "POST", body: { hub }, auth: true });
export const applyConsolidation = (hub, article) => request("/admin/openseo/consolidation/apply", { method: "POST", body: { hub, article }, auth: true });
// Growth plan: ranking targets, their actions, GSC position history, content.
export const fetchGrowthPlan = (refresh = false) => request(`/admin/openseo/plan${refresh ? "?refresh=true" : ""}`, { auth: true });
export const seedGrowthPlan = () => request("/admin/openseo/plan/seed", { method: "POST", auth: true });
export const addGrowthPlanItem = (body) => request("/admin/openseo/plan", { method: "POST", body, auth: true });
export const patchGrowthPlanItem = (id, body) => request(`/admin/openseo/plan/${encodeURIComponent(id)}`, { method: "PATCH", body, auth: true });
export const deleteGrowthPlanItem = (id) => request(`/admin/openseo/plan/${encodeURIComponent(id)}`, { method: "DELETE", auth: true });
export const planGrowthContent = (id) => request(`/admin/openseo/plan/${encodeURIComponent(id)}/content`, { method: "POST", auth: true });
export const fetchSeoOpportunities = (days = 90) => request(`/admin/seo/opportunities?days=${days}`, { auth: true });
export const fetchIndexingStatus = () => request("/admin/seo/indexing-status", { auth: true });
export const runIndexingNow = () => request("/admin/seo/indexing-run", { method: "POST", auth: true });

// ---- Rank tracker ----
export const fetchRankKeywords = () => request("/admin/seo/keywords", { auth: true });
export const addRankKeyword = (keyword) => request("/admin/seo/keywords", { method: "POST", auth: true, body: { keyword } });
export const deleteRankKeyword = (keyword) => request(`/admin/seo/keywords/${encodeURIComponent(keyword)}`, { method: "DELETE", auth: true });
export const refreshRankKeywords = () => request("/admin/seo/keywords/refresh", { method: "POST", auth: true });
export const gscDiagnose = () => request("/admin/seo/gsc-diagnose", { auth: true });
export const gscSetProperty = (property) => request("/admin/seo/gsc-property", { method: "POST", auth: true, body: { property } });

// ---- Brand voice (auto-detected from our own published articles) ----
export const fetchBrandVoice = () => request("/admin/seo/brand-voice", { auth: true });
export const refreshBrandVoice = () => request("/admin/seo/brand-voice/refresh", { method: "POST", auth: true });

// ---- External publisher ingest (Soro etc.) ----
export const fetchPublisherIntegration = () => request("/admin/integrations/publisher", { auth: true });
export const updatePublisherIntegration = (patch) => request("/admin/integrations/publisher", { method: "POST", auth: true, body: patch });

// ---- Google Ads (Keyword Planner search volume) ----
export const fetchAdsConfig = () => request("/admin/seo/ads/config", { auth: true });
export const saveAdsConfig = (cfg) => request("/admin/seo/ads/config", { method: "POST", auth: true, body: cfg });
export const disconnectAds = () => request("/admin/seo/ads/disconnect", { method: "POST", auth: true });
export const startAdsConnect = () => request("/admin/seo/ads/oauth/start", { method: "POST", auth: true });
export const fetchAdsKeywordIdeas = (keywords) => request("/admin/seo/ads/keyword-ideas", { method: "POST", auth: true, body: keywords ? { keywords } : {} });

// ---- WhatsApp lead alerts ----
export const whatsappTest = () => request("/admin/notify/whatsapp-test", { method: "POST", auth: true });

// ---- Site health ----
export const fetchHealth = () => request("/admin/health", { auth: true });

// ---- Active sessions ----
export const fetchSessions = () => request("/admin/sessions", { auth: true });
export const revokeSession = (jti) => request(`/admin/sessions/${jti}`, { method: "DELETE", auth: true });

// ---- Media cleanup ----
export const deleteUnusedMedia = () => request("/admin/media/unused/all", { method: "DELETE", auth: true });

// ---- Quotes ----
export const fetchQuotes = (leadId) => request(`/admin/quotes${leadId ? `?leadId=${encodeURIComponent(leadId)}` : ""}`, { auth: true });
export const createQuote = (payload) => request("/admin/quotes", { method: "POST", auth: true, body: payload });
export const sendQuote = (id) => request(`/admin/quotes/${id}/send`, { method: "POST", auth: true });
export const setQuoteStatus = (id, status) => request(`/admin/quotes/${id}`, { method: "PATCH", auth: true, body: { status } });
export const deleteQuote = (id) => request(`/admin/quotes/${id}`, { method: "DELETE", auth: true });
export async function downloadQuotePdf(id, number) {
  const res = await fetch(`${API}/admin/quotes/${id}/pdf`, { headers: { Authorization: `Bearer ${getToken()}` } });
  if (!res.ok) throw new Error(`PDF failed (${res.status})`);
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = `${number || "quote"}.pdf`;
  document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 10000);
}

// ---- Full-site backup ----
// Raw fetch (not request()): the export streams a zip blob and the import
// posts multipart form data — neither fits the JSON helper.
export async function downloadBackup() {
  const res = await fetch(`${API}/admin/backup/export`, { headers: { Authorization: `Bearer ${getToken()}` } });
  if (!res.ok) throw new Error(res.status === 403 ? "Only the administrator account can export backups." : `Backup failed (${res.status})`);
  const blob = await res.blob();
  const cd = res.headers.get("content-disposition") || "";
  const name = /filename="?([^";]+)/.exec(cd)?.[1] || "clisecure-backup.zip";
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = name;
  document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 10000);
  return name;
}
export async function uploadBackup(file) {
  const fd = new FormData();
  fd.append("file", file);
  const res = await fetch(`${API}/admin/backup/import`, {
    method: "POST", headers: { Authorization: `Bearer ${getToken()}` }, body: fd,
  });
  if (!res.ok) {
    let detail = `Restore failed (${res.status})`;
    try { const j = await res.json(); if (j?.detail) detail = j.detail; } catch { /* ignore */ }
    throw new Error(detail);
  }
  return res.json();
}

// ---- OneDrive auto-backup (device-code sign-in + nightly upload) ----
export const fetchOnedriveConfig = () => request("/admin/backup/onedrive/config", { auth: true });
export const saveOnedriveConfig = (cfg) => request("/admin/backup/onedrive/config", { method: "POST", auth: true, body: cfg });
export const disconnectOnedrive = () => request("/admin/backup/onedrive/disconnect", { method: "POST", auth: true });
export const startOnedriveConnect = () => request("/admin/backup/onedrive/connect/start", { method: "POST", auth: true });
export const getOnedriveConnectJob = (jobId) => request(`/admin/backup/onedrive/connect/job/${jobId}`, { auth: true });
export const runOnedriveBackupNow = () => request("/admin/backup/onedrive/run-now", { method: "POST", auth: true });

// ---- Sitemap ping / IndexNow (admin) ----
export const pingSitemap = () => request("/admin/seo/ping-sitemap", { method: "POST", auth: true });

// ---- Broken-link checker (admin) ----
export const checkLinks = () => request("/admin/tools/link-check", { method: "POST", auth: true });

// ---- Auth ----
export const login = (email, password, code, rememberDevice = false) =>
  request("/admin/login", {
    method: "POST",
    body: {
      email,
      password,
      code: code || undefined,
      device_token: getDeviceToken(email) || undefined,
      remember_device: !!rememberDevice,
    },
  });
export const fetchMe = () => request("/admin/me", { auth: true });
export const changePassword = (current_password, new_password) =>
  request("/admin/change-password", { method: "POST", auth: true, body: { current_password, new_password } });

// ---- Two-factor auth ----
export const twofaStatus = () => request("/admin/2fa/status", { auth: true });
export const twofaSetup = () => request("/admin/2fa/setup", { method: "POST", auth: true });
export const twofaEnable = (code) => request("/admin/2fa/enable", { method: "POST", auth: true, body: { code } });
export const twofaDisable = (code) => request("/admin/2fa/disable", { method: "POST", auth: true, body: { code } });

// ---- Trusted devices (browsers that skip the 2FA prompt) ----
export const twofaDevices = () => request("/admin/2fa/devices", { auth: true });
export const forgetTrustedDevice = (id) => request(`/admin/2fa/devices/${encodeURIComponent(id)}`, { method: "DELETE", auth: true });
export const forgetAllTrustedDevices = () => request("/admin/2fa/devices", { method: "DELETE", auth: true });

// ---- Audit log ----
export const fetchAudit = () => request("/admin/audit", { auth: true });

// ---- Notifications ----
export const notifyConfig = () => request("/admin/notify/config", { auth: true });
export const sendTestNotification = () => request("/admin/notify/test", { method: "POST", auth: true });

// ---- AI assistant ----
export const aiStatus = () => request("/admin/ai/status", { auth: true });
export const aiGenerate = (payload) => request("/admin/ai", { method: "POST", auth: true, body: payload });

// Async AI generation for long tasks (article drafts): create a background job
// and poll until it finishes, so no single request can hit an edge/proxy
// timeout. Returns the same result shape as aiGenerate.
export const createAiJob = (payload) => request("/admin/ai/job", { method: "POST", auth: true, body: payload });
export const getAiJob = (jobId) => request(`/admin/ai/job/${jobId}`, { auth: true });
export async function aiGenerateAsync(payload, { intervalMs = 2500, timeoutMs = 240000 } = {}) {
  const { jobId } = await createAiJob(payload);
  const start = Date.now();
  // small initial delay before first poll
  for (;;) {
    await new Promise((r) => setTimeout(r, intervalMs));
    let job;
    try {
      job = await getAiJob(jobId);
    } catch (e) {
      // A transient poll failure shouldn't abort a job that's still running.
      if (Date.now() - start > timeoutMs) throw e;
      continue;
    }
    if (job.status === "done") return job.result;
    if (job.status === "error") throw new Error(job.error || "Generation failed.");
    if (Date.now() - start > timeoutMs) throw new Error("Generation is taking too long — please try again.");
  }
}
export const generateImage = (prompt, size = "1536x1024") =>
  request("/admin/ai/image", { method: "POST", auth: true, body: { prompt, size } }).then((d) => ({ ...d, url: absoluteUrl(d.url) }));

// ---- AI video (branded slides + voiceover, cut into an MP4) ----
export const createVideoJob = (payload) => request("/admin/video/job", { method: "POST", auth: true, body: payload });
export const getVideoJob = (jobId) => request(`/admin/video/job/${jobId}`, { auth: true });
export async function videoGenerateAsync(payload, { onStep, intervalMs = 3000, timeoutMs = 360000 } = {}) {
  const { jobId } = await createVideoJob(payload);
  const start = Date.now();
  for (;;) {
    await new Promise((r) => setTimeout(r, intervalMs));
    let job;
    try {
      job = await getVideoJob(jobId);
    } catch (e) {
      if (Date.now() - start > timeoutMs) throw e;
      continue;
    }
    if (job.step && onStep) onStep(job.step);
    if (job.status === "done") return { ...job.result, url: absoluteUrl(job.result.url) };
    if (job.status === "error") throw new Error(job.error || "Video generation failed.");
    if (Date.now() - start > timeoutMs) throw new Error("Video generation is taking too long — please try again.");
  }
}

// ---- Users (administrators only) ----
export const fetchUsers = () => request("/admin/users", { auth: true });
export const createUser = (email, password, role) => request("/admin/users", { method: "POST", auth: true, body: { email, password, role } });
export const updateUserRole = (email, role) => request(`/admin/users/${encodeURIComponent(email)}`, { method: "PATCH", auth: true, body: { role } });
export const resetUserPassword = (email, new_password) => request(`/admin/users/${encodeURIComponent(email)}/reset-password`, { method: "POST", auth: true, body: { new_password } });
export const resetUserTwofa = (email) => request(`/admin/users/${encodeURIComponent(email)}/reset-2fa`, { method: "POST", auth: true });
export const deleteUser = (email) => request(`/admin/users/${encodeURIComponent(email)}`, { method: "DELETE", auth: true });

// ---- Leads ----
const qs = (params = {}) => {
  const usp = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => { if (v !== undefined && v !== null && v !== "") usp.set(k, v); });
  const s = usp.toString();
  return s ? `?${s}` : "";
};
// Paginated/filterable listing → { items, total, counts }.
// ---- Social sharing queue ----
export const fetchSocialConfig = () => request("/admin/social/config", { auth: true });
export const saveSocialConfig = (cfg) => request("/admin/social/config", { method: "POST", auth: true, body: cfg });
export const fetchSocialQueue = () => request("/admin/social/queue", { auth: true });
export const socialCaptions = (slug, body) => request(`/admin/social/queue/${encodeURIComponent(slug)}/captions`, { method: "POST", auth: true, body });
export const socialSend = (slug, body) => request(`/admin/social/queue/${encodeURIComponent(slug)}/send`, { method: "POST", auth: true, body });
export const socialPublish = (slug, payload) => request(`/admin/social/queue/${slug}/publish`, { method: "POST", auth: true, body: payload });
export const socialTestConnection = (network) => request("/admin/social/connections/test", { method: "POST", auth: true, body: { network } });
export const socialGenerate = (body) => request("/admin/social/generate", { method: "POST", auth: true, body });
export const socialMakeCard = (slug, body) => request(`/admin/social/queue/${encodeURIComponent(slug)}/card`, { method: "POST", auth: true, body });
export const socialSetStatus = (slug, status) => request(`/admin/social/queue/${encodeURIComponent(slug)}/status`, { method: "POST", auth: true, body: { status } });

// ---- YouTube (upload the AI-generated video straight to the channel) ----
export const fetchYoutubeConfig = () => request("/admin/social/youtube/config", { auth: true });
export const saveYoutubeConfig = (cfg) => request("/admin/social/youtube/config", { method: "POST", auth: true, body: cfg });
export const disconnectYoutube = () => request("/admin/social/youtube/disconnect", { method: "POST", auth: true });
export const startYoutubeConnect = () => request("/admin/social/youtube/oauth/start", { method: "POST", auth: true });
export const uploadToYoutube = (payload) => request("/admin/social/youtube/upload", { method: "POST", auth: true, body: payload });

export const fetchLeadsPage = (params = {}) => request(`/admin/leads${qs(params)}`, { auth: true });
// Back-compat: full array of leads (used by Analytics/Dashboard aggregates).
export const fetchLeads = () => fetchLeadsPage().then((r) => (Array.isArray(r) ? r : r.items || []));
export const bulkLeads = (ids, op, status) => request("/admin/leads/bulk", { method: "POST", auth: true, body: { ids, op, status } });
export const deleteLead = (id) => request(`/admin/leads/${id}`, { method: "DELETE", auth: true });
export const updateLead = (id, patch) => request(`/admin/leads/${id}`, { method: "PATCH", auth: true, body: patch });
export const addLeadNote = (id, text) => request(`/admin/leads/${id}/notes`, { method: "POST", auth: true, body: { text } });
export const replyLead = (id, payload) => request(`/admin/leads/${id}/reply`, { method: "POST", auth: true, body: payload });

// ---- Content (admin) ----
export const fetchAdminContent = () => request("/admin/content", { auth: true });
export const saveContent = (key, data, baseRev) =>
  request(`/admin/content/${key}`, { method: "PUT", auth: true, body: { data, base_rev: baseRev } });
export const fixSeoArticle = (slug, opts) => request(`/admin/seo/audit/fix-article`, { method: "POST", auth: true, body: { slug, ...opts } });
export const fetchVersions = (key) => request(`/admin/content/${key}/versions`, { auth: true });
export const fetchVersion = (key, id) => request(`/admin/content/${key}/versions/${id}`, { auth: true });
export const restoreVersion = (key, id) => request(`/admin/content/${key}/restore/${id}`, { method: "POST", auth: true });

// ---- Media ----
export async function uploadMedia(file) {
  const fd = new FormData();
  fd.append("file", file);
  const headers = {};
  const t = getToken();
  if (t) headers.Authorization = `Bearer ${t}`;
  const res = await fetch(`${API}/admin/media`, { method: "POST", headers, body: fd });
  if (!res.ok) {
    let detail = `Upload failed (${res.status})`;
    try {
      const j = await res.json();
      if (j && j.detail) detail = j.detail;
    } catch {
      /* ignore */
    }
    throw new Error(detail);
  }
  const data = await res.json();
  return { ...data, url: absoluteUrl(data.url) };
}
export const fetchMedia = () =>
  request("/admin/media", { auth: true }).then((rows) => rows.map((r) => ({ ...r, url: absoluteUrl(r.url) })));
export const deleteMedia = (id) => request(`/admin/media/${id}`, { method: "DELETE", auth: true });
export const renameMedia = (id, filename) => request(`/admin/media/${id}`, { method: "PATCH", auth: true, body: { filename } });
export const importMedia = (url) =>
  request("/admin/media/import", { method: "POST", auth: true, body: { url } }).then((d) => ({ ...d, url: absoluteUrl(d.url) }));
export const localizeContentImages = () => request("/admin/media/localize-content", { method: "POST", auth: true });

// ── Multi-site portal — sister sites managed through the backend proxy ──
export const fetchRemoteSites = () => request("/admin/remote/sites", { auth: true });
export const remoteGet = (site, path) => request(`/admin/remote/${site}/${path}`, { auth: true, keepSession: true });
export const remoteSend = (site, path, method, body) =>
  request(`/admin/remote/${site}/${path}`, { method, auth: true, body, keepSession: true });

// Remote-site SEO engine (indexing / Search Console / rank tracker for a sister site).
export const fetchRemoteSeoConfig = (site) => request(`/admin/remote-seo/${site}/config`, { auth: true });
export const setRemoteGscProperty = (site, property) =>
  request(`/admin/remote-seo/${site}/gsc-property`, { method: "POST", auth: true, body: { property } });
export const fetchRemoteSearchConsole = (site, days = 28) =>
  request(`/admin/remote-seo/${site}/search-console?days=${days}`, { auth: true });
export const fetchRemoteIndexingStatus = (site) => request(`/admin/remote-seo/${site}/indexing-status`, { auth: true });
export const runRemoteIndexingNow = (site) => request(`/admin/remote-seo/${site}/indexing-run`, { method: "POST", auth: true });
export const fetchRemoteAudit = (site) => request(`/admin/remote-seo/${site}/audit`, { auth: true });
export const runRemoteAudit = (site) => request(`/admin/remote-seo/${site}/audit`, { method: "POST", auth: true });
export const fetchRemoteRankKeywords = (site) => request(`/admin/remote-seo/${site}/keywords`, { auth: true });
export const addRemoteRankKeyword = (site, keyword) =>
  request(`/admin/remote-seo/${site}/keywords`, { method: "POST", auth: true, body: { keyword } });
export const deleteRemoteRankKeyword = (site, keyword) =>
  request(`/admin/remote-seo/${site}/keywords/${encodeURIComponent(keyword)}`, { method: "DELETE", auth: true });
export const refreshRemoteRankKeywords = (site) =>
  request(`/admin/remote-seo/${site}/keywords/refresh`, { method: "POST", auth: true });

// Remote-site Content Studio (daily AI publisher pointed at a sister site's blog).
export const fetchRemoteContentStudio = (site) => request(`/admin/remote-seo/${site}/content-studio`, { auth: true });
export const saveRemoteContentStudio = (site, cfg) =>
  request(`/admin/remote-seo/${site}/content-studio/config`, { method: "POST", auth: true, body: cfg });
export const runRemoteContentStudio = (site) =>
  request(`/admin/remote-seo/${site}/content-studio/run`, { method: "POST", auth: true });
export const replenishRemoteBacklog = (site) =>
  request(`/admin/remote-seo/${site}/content-studio/replenish`, { method: "POST", auth: true });
export const deleteRemoteBacklogTopic = (site, slug) =>
  request(`/admin/remote-seo/${site}/content-studio/backlog/${encodeURIComponent(slug)}`, { method: "DELETE", auth: true });
export const addRemoteBacklogTopic = (site, topic) =>
  request(`/admin/remote-seo/${site}/content-studio/backlog`, { method: "POST", auth: true, body: topic });
export const prioritiseRemoteBacklogTopic = (site, slug) =>
  request(`/admin/remote-seo/${site}/content-studio/backlog/${encodeURIComponent(slug)}/prioritise`, { method: "POST", auth: true });

export const fetchRemoteBrandVoice = (site) => request(`/admin/remote-seo/${site}/brand-voice`, { auth: true });
export const refreshRemoteBrandVoice = (site) => request(`/admin/remote-seo/${site}/brand-voice/refresh`, { method: "POST", auth: true });

// One-click AI fix for a page in the sister site's last audit — the AI writes
// the fix straight to that site's CMS and the stored audit row is updated.
// fix: "meta" | "keyword" | "thin" | "links" | "alt"
export const fixRemoteAuditPage = (site, path, fix) =>
  request(`/admin/remote-seo/${site}/audit/fix`, { method: "POST", auth: true, body: { path, fix } });

// AI image generated ON the sister site (saved into its media library) — the
// URL comes back site-relative, so make it absolute for previews.
export const generateRemoteImage = (site, prompt, { width = 1200, height = 800 } = {}) =>
  request(`/admin/remote/${site}/admin-v2/ai/image`, { method: "POST", auth: true, keepSession: true, body: { prompt, width, height, folder: "ai-images" } });

export { API, BACKEND };
